import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// ── スクロールアニメーション hook ───────────────────────────────────
const useInView = (threshold = 0.15) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
            { threshold }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
};

// 日本語見出しの改行位置調整：単語の途中で折り返されないよう、単語ごとにnowrapで囲む
const NoBreakWords = ({ words }) => (
    <>
        {words.map((w, i) => (
            <span key={i} style={{ whiteSpace: 'nowrap' }}>{w}</span>
        ))}
    </>
);

// ── SVG アイコン ────────────────────────────────────────────────────
const MsgIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);
const FileTextIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" />
    </svg>
);
const ClipboardIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="8" y="2" width="8" height="4" rx="1" /><polyline points="9 13 11 15 15 11" />
    </svg>
);
const LayoutGridIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
    </svg>
);
const FolderIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
);
const CreditCardIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
    </svg>
);
const ShieldCheckIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" />
    </svg>
);
const CheckSquareIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
);
const BanknoteIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2" /><path d="M6 12h.01M18 12h.01" />
    </svg>
);
const ArrowRightIcon = ({ color = '#005c53', size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);
const ChevronDownIcon = ({ open }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
        <polyline points="6 9 12 15 18 9" />
    </svg>
);
const CheckIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const CheckTealIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

// ── データ ──────────────────────────────────────────────────────────
// STEP2「のりいれ三丁目が施工業者に代わって受領」は収納代行スキーム（payment_scheme_draft.md）の
// 正式な整理に沿った表現。「預かる」等の留保・保管を示す custody 表現は法務判断により使用しない。
const paymentSteps = [
    { Icon: CreditCardIcon,   step: 'STEP 1', title: 'お客様が代金を\nお支払い',             bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)' },
    { Icon: ShieldCheckIcon,  step: 'STEP 2', title: 'のりいれ三丁目が\n施工業者に代わって受領', bg: 'linear-gradient(135deg,#fffbeb,#fef3c7)' },
    { Icon: CheckSquareIcon,  step: 'STEP 3', title: '工事完了を\nお客様が確認',               bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)' },
    { Icon: BanknoteIcon,     step: 'STEP 4', title: '施工業者へ\n代金をお振込み',             bg: 'linear-gradient(135deg,#faf5ff,#ede9fe)' },
];

const features = [
    { Icon: MsgIcon,            effect: 'お客様との連絡が、ひとつにまとまる',         desc: 'チャットで見積もり依頼から現場の相談まで一元管理', iconBg: 'linear-gradient(135deg,#e6f2f1,#d1f5f0)' },
    { Icon: FileTextIcon,       effect: '見積書は、入力するだけで完成',               desc: '必要項目を入力するだけで、きれいな見積書が即座に作成できます', iconBg: 'linear-gradient(135deg,#fef3c7,#fde68a)' },
    { Icon: ClipboardIcon,      effect: '請求書も、見積内容を引き継いでスムーズに',   desc: '見積書の内容を自動で反映。二度手間なく請求書に変換できます', iconBg: 'linear-gradient(135deg,#e0e7ff,#c7d2fe)' },
    { Icon: CheckSquareIcon,    effect: '次に何をするか、迷わない',                   desc: 'やることリストに沿って対応するだけ。大切な手順を見逃しません', iconBg: 'linear-gradient(135deg,#d1fae5,#a7f3d0)' },
    { Icon: LayoutGridIcon,     effect: 'お客様ごとの状況が、すぐわかる',             desc: '案件管理画面で全案件の進捗を一覧表示。対応漏れを防ぎます', iconBg: 'linear-gradient(135deg,#fce7f3,#fbcfe8)' },
    { Icon: FolderIcon,         effect: '書類を探す手間が、なくなる',                 desc: '見積書・請求書をまとめて管理。いつでも過去の書類を確認できます', iconBg: 'linear-gradient(135deg,#ffedd5,#fed7aa)' },
];

const workflowSteps = [
    { n: '1', title: 'お客様から\n相談が届く',   desc: 'チャットで通知',   color: '#3b82f6' },
    { n: '2', title: 'チャットで\n内容を確認',   desc: '疑問点も気軽に',   color: '#0ea5e9' },
    { n: '3', title: '見積書を\n作成・送信',     desc: '入力するだけ',     color: '#f59e0b' },
    { n: '4', title: 'お客様が\n代金をお支払い', desc: '工事前に先払い',   color: '#10b981' },
    { n: '5', title: '工事を実施',               desc: '現場の仕事に集中', color: '#8b5cf6' },
    { n: '6', title: '請求・\n完了処理',         desc: 'スムーズに手続き', color: '#ec4899' },
    { n: '7', title: '入金',                     desc: '完了確認後お振込', color: '#005c53' },
];

const beforeItems = [
    '電話やLINEでバラバラにやり取り',
    'Excelで見積書・請求書を別々に作成',
    'どのお客様がどこまで進んでいるか分かりづらい',
    '入金されるまで不安',
    '新規のお客様探し（営業）にも時間がかかる',
];
const afterItems = [
    'チャットで一元管理',
    '見積書の内容を引き継いで請求書も簡単作成',
    'やることリストで次の対応がすぐ分かる',
    '事前のお支払いで、入金も安心',
    'お客様からの相談が直接届き、営業の負担を削減',
];

const faqs = [
    { q: '利用料金はかかりますか？',                     a: '登録は無料です。成約時に工事代金の20%を手数料として申し受けます。' },
    { q: '審査はありますか？',                           a: '事前に簡単な審査を行い、承認後にご利用いただけます。' },
    { q: '代金はいつ受け取れますか？',                   a: '工事完了をお客様が確認した後、お振込みいたします。' },
    { q: '対応エリアや対応内容に制限はありますか？',     a: '対応エリア・対応可能な工事内容はご登録時に設定いただけます。' },
    { q: 'パソコンが苦手でも使えますか？',               a: 'スマートフォンからも簡単に操作でき、次にやることが分かるシンプルな設計です。' },
    { q: '今すぐお客様からの依頼はありますか？',         a: '登録後、対応エリア内のお客様からチャットで直接ご相談が届きます。' },
];

// ── フェードインセクション ──────────────────────────────────────────
const FadeSection = ({ children, delay = 0, style = {} }) => {
    const [ref, inView] = useInView();
    return (
        <div ref={ref} style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(28px)',
            transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
            ...style,
        }}>
            {children}
        </div>
    );
};

// ── メイン ─────────────────────────────────────────────────────────
const ContractorLanding = () => {
    const [openFaq, setOpenFaq] = useState(null);

    return (
        <div style={{ background: '#f5f6f8' }}>
            <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(24px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes pulseDot {
                    0%,100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.4); opacity: 0.6; }
                }
                @keyframes orbBreath1 {
                    0%,100% { transform: scale(1);   opacity: 0.12; }
                    50%     { transform: scale(1.25); opacity: 0.22; }
                }
                @keyframes orbBreath2 {
                    0%,100% { transform: scale(1);   opacity: 0.06; }
                    50%     { transform: scale(1.35); opacity: 0.16; }
                }
                .clp-hero-1 { animation: fadeInUp 0.6s ease 0.1s both; }
                .clp-hero-2 { animation: fadeInUp 0.6s ease 0.25s both; }
                .clp-hero-3 { animation: fadeInUp 0.6s ease 0.4s both; }
                .clp-hero-4 { animation: fadeInUp 0.6s ease 0.55s both; }
                .clp-hero-img { animation: fadeInUp 0.8s ease 0.35s both; }
                .clp-feature-card {
                    background: #fff; border: 1px solid #e0e4ea; border-radius: 12px;
                    padding: 28px 24px; transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
                    cursor: default; height: 100%; box-sizing: border-box;
                }
                .clp-feature-card:hover { transform: translateY(-8px) scale(1.02); box-shadow: 0 20px 40px rgba(0,92,83,0.12); border-color: transparent; }
                .clp-pay-card {
                    border-radius: 12px; padding: 24px 20px; text-align: center; border: 2px solid #fff;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.06); transition: transform 0.3s ease, box-shadow 0.3s ease;
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                }
                .clp-pay-card:hover { transform: scale(1.06); box-shadow: 0 12px 32px rgba(0,0,0,0.12); }
                .clp-step-circle {
                    border-radius: 50%; color: #fff; font-weight: 700;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 16px; transition: transform 0.5s ease;
                    width: 56px; height: 56px; font-size: 1.2rem; flex-shrink: 0;
                }
                .clp-step-wrap:hover .clp-step-circle { transform: scale(1.15) rotate(360deg); }
                .clp-step-wrap { transition: transform 0.25s ease; text-align: center; }
                .clp-step-wrap:hover { transform: translateY(-8px); }
                .clp-faq-item { border: 2px solid #e0e4ea; border-radius: 12px; overflow: hidden; background: #fff; transition: border-color 0.2s, box-shadow 0.2s; }
                .clp-faq-item:hover { border-color: rgba(0,92,83,0.3); box-shadow: 0 4px 16px rgba(0,0,0,0.06); }
                .clp-faq-btn { width: 100%; display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; background: none; border: none; cursor: pointer; text-align: left; transition: background 0.2s; }
                .clp-faq-btn:hover { background: linear-gradient(90deg,rgba(230,242,241,0.4),transparent); }
                .clp-faq-chevron { flex-shrink: 0; background: #e6f2f1; border-radius: 50%; padding: 6px; display: flex; align-items: center; justify-content: center; transition: background 0.2s; color: #005c53; }
                .clp-faq-btn:hover .clp-faq-chevron { background: #005c53; color: #fff; }
                .clp-faq-answer { overflow: hidden; transition: max-height 0.35s ease, opacity 0.35s ease; }
                .clp-cta-primary {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 14px 28px; border-radius: 8px; font-weight: 700; font-size: 1rem;
                    background: linear-gradient(135deg,#00a99d,#005c53); color: #fff; border: none; cursor: pointer;
                    box-shadow: 0 8px 24px rgba(0,169,157,0.25); transition: transform 0.2s, box-shadow 0.2s;
                    text-decoration: none;
                }
                .clp-cta-primary:hover { transform: scale(1.05); box-shadow: 0 12px 32px rgba(0,169,157,0.35); color: #fff; }
                .clp-cta-outline {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 14px 28px; border-radius: 8px; font-weight: 700; font-size: 1rem;
                    background: #fff; color: #005c53; border: 2px solid #005c53; cursor: pointer;
                    transition: background 0.2s, color 0.2s, transform 0.2s; text-decoration: none;
                }
                .clp-cta-outline:hover { background: #005c53; color: #fff; transform: scale(1.05); }
                .clp-cta-final {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 16px 32px; border-radius: 8px; font-weight: 700; font-size: 1rem;
                    background: #fff; color: #005c53; border: none; cursor: pointer;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.15); transition: transform 0.2s, box-shadow 0.2s;
                    text-decoration: none;
                }
                .clp-cta-final:hover { transform: scale(1.05); box-shadow: 0 8px 24px rgba(0,0,0,0.2); color: #005c53; }
                .clp-float-1 { position: absolute; bottom: -16px; left: -16px; background: rgba(255,255,255,0.95); backdrop-filter: blur(8px); border-radius: 12px; padding: 14px 18px; box-shadow: 0 8px 32px rgba(0,0,0,0.12); border: 2px solid rgba(0,92,83,0.1); }
                .clp-float-2 { position: absolute; top: -16px; right: -16px; background: linear-gradient(135deg,#fbbf24,#f59e0b); border-radius: 12px; padding: 14px 18px; box-shadow: 0 8px 32px rgba(0,0,0,0.12); }
                @media (max-width: 768px) {
                    .clp-hero-grid { grid-template-columns: 1fr !important; }
                    .clp-hero-img-col { display: none !important; }
                    .clp-features-grid { grid-template-columns: 1fr !important; }
                    .clp-pay-grid { grid-template-columns: 1fr 1fr !important; }
                    .clp-workflow-grid { grid-auto-flow: column; grid-auto-columns: minmax(120px,1fr); overflow-x: auto; padding-bottom: 8px; }
                    .clp-compare-grid { grid-template-columns: 1fr !important; }
                    .clp-final-inner { flex-direction: column !important; text-align: center !important; }
                }
            `}</style>

            {/* ── Hero ─────────────────────────────────────────────── */}
            <section style={{
                background: 'linear-gradient(135deg,#f0fffe 0%,#ffffff 50%,rgba(254,243,199,0.3) 100%)',
                overflow: 'hidden', position: 'relative',
            }}>
                <div style={{
                    position: 'absolute', inset: 0, opacity: 0.04,
                    backgroundImage: 'linear-gradient(#005c53 1px,transparent 1px),linear-gradient(90deg,#005c53 1px,transparent 1px)',
                    backgroundSize: '50px 50px',
                }} />
                <div style={{ position: 'absolute', top: 0, right: '20%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle,rgba(0,169,157,0.1),transparent)', filter: 'blur(60px)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: 0, left: '20%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle,rgba(251,191,36,0.08),transparent)', filter: 'blur(60px)', pointerEvents: 'none' }} />

                <div style={{ maxWidth: 1080, margin: '0 auto', padding: '80px 24px 96px', position: 'relative' }}>
                    <div className="clp-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
                        <div>
                            <div className="clp-hero-1" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', background: 'linear-gradient(90deg,#fbbf24,#f59e0b,#fbbf24)', borderRadius: 20, fontSize: '0.82rem', fontWeight: 700, color: '#141c3d', marginBottom: 24 }}>
                                <span style={{ width: 8, height: 8, background: '#141c3d', borderRadius: '50%', animation: 'pulseDot 1.8s ease infinite' }} />
                                施工業者様 募集中
                            </div>

                            <h1 className="clp-hero-2" style={{ fontSize: 'clamp(1.9rem,4.2vw,3rem)', fontWeight: 900, color: '#141c3d', lineHeight: 1.3, marginBottom: 12, textWrap: 'balance' }}>
                                <NoBreakWords words={['工事は', 'ちゃんと', '終わった。']} /><br />
                                <span style={{ color: '#6b7280', fontWeight: 700 }}>
                                    <NoBreakWords words={['でも、', 'お金が', '振り込まれない。']} />
                                </span>
                            </h1>
                            <h2 style={{ fontSize: 'clamp(1.4rem,3vw,2.1rem)', fontWeight: 900, lineHeight: 1.3, marginBottom: 20, textWrap: 'balance' }}>
                                <span style={{ background: 'linear-gradient(90deg,#00a99d,#005c53,#004540)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                    <NoBreakWords words={['そんな心配を、', 'なくす', '仕組みです。']} />
                                </span>
                            </h2>

                            <p className="clp-hero-3" style={{ fontSize: '1.05rem', color: '#6b7280', lineHeight: 1.8, marginBottom: 32, maxWidth: 480 }}>
                                お客様とのやり取り、見積書・請求書の作成、進捗管理、代金の受け取りまで。面倒な事務作業をひとつにまとめて、現場の仕事に集中できる環境をつくります。
                            </p>

                            <div className="clp-hero-4">
                                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
                                    <Link to="/contractor/login?tab=register" className="clp-cta-primary">
                                        無料で施工業者登録する <ArrowRightIcon color="#fff" />
                                    </Link>
                                    <a href="#flow" className="clp-cta-outline">
                                        サービスの流れを見る
                                    </a>
                                </div>
                                <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: 0 }}>登録は数分で完了・営業の手間も削減</p>
                            </div>
                        </div>

                        <div className="clp-hero-img-col clp-hero-img" style={{ position: 'relative' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                <div style={{ gridColumn: '1 / -1', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 48px rgba(0,0,0,0.14)', border: '2px solid rgba(0,92,83,0.08)', position: 'relative' }}>
                                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,rgba(0,169,157,0.08),transparent)', zIndex: 1 }} />
                                    <img src="/images/contractor-top-01.jpeg" alt="お客様との打ち合わせ" style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }} />
                                </div>
                                <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 32px rgba(0,0,0,0.1)', border: '2px solid rgba(251,191,36,0.2)', position: 'relative' }}>
                                    <img src="/images/contractor-top-02.jpeg" alt="スマートフォンで確認する施工業者" style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }} />
                                </div>
                                <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 32px rgba(0,0,0,0.1)', border: '2px solid rgba(16,185,129,0.2)', position: 'relative' }}>
                                    <img src="/images/contractor-top-03.jpeg" alt="工事現場" style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }} />
                                </div>
                            </div>
                            <div className="clp-float-1">
                                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#005c53' }}>安心決済</div>
                                <div style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 500 }}>安心決済対応</div>
                            </div>
                            <div className="clp-float-2">
                                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#fff' }}>事務作業削減</div>
                                <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>書類まとめて管理</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── 安心セクション ─────────────────────────────────────── */}
            <section style={{ background: 'linear-gradient(135deg,#f5f6f8,rgba(254,243,199,0.15),#f5f6f8)', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle at 20px 20px,#005c53 1px,transparent 0)', backgroundSize: '40px 40px' }} />
                <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
                    <FadeSection style={{ textAlign: 'center', marginBottom: 56 }}>
                        <div style={{ display: 'inline-block', padding: '6px 18px', background: 'linear-gradient(90deg,#fbbf24,#f59e0b)', color: '#fff', borderRadius: 20, fontSize: '0.82rem', fontWeight: 700, marginBottom: 16 }}>
                            安全な決済システム
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.1rem)', fontWeight: 700, color: '#1a1a2e', marginBottom: 14 }}>
                            工事をしたのに、支払ってもらえない。<br />その不安を減らします。
                        </h2>
                        <p style={{ fontSize: '1rem', color: '#6b7280', maxWidth: 620, margin: '0 auto', lineHeight: 1.8 }}>
                            お客様には工事前に代金のお支払いを完了いただく仕組みなので、工事完了後に「なかなか振り込まれない」「連絡が取れない」といった心配がなくなります。
                        </p>
                    </FadeSection>

                    <div className="clp-pay-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20 }}>
                        {paymentSteps.map(({ Icon, step, title, bg }, i) => (
                            <FadeSection key={i} delay={i * 0.12}>
                                <div className="clp-pay-card" style={{ background: bg }}>
                                    <div style={{ display: 'inline-flex', padding: 14, background: 'rgba(255,255,255,0.7)', borderRadius: 14, marginBottom: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                        <Icon />
                                    </div>
                                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#005c53', marginBottom: 8, letterSpacing: '0.05em' }}>{step}</div>
                                    <p style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1a1a2e', whiteSpace: 'pre-line', margin: 0, lineHeight: 1.6 }}>{title}</p>
                                </div>
                            </FadeSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── 機能紹介 ───────────────────────────────────────────── */}
            <section style={{ background: '#fff', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, right: 0, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle,rgba(0,169,157,0.05),transparent)', filter: 'blur(40px)' }} />
                <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
                    <FadeSection style={{ textAlign: 'center', marginBottom: 56 }}>
                        <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, marginBottom: 12 }}>
                            <span style={{ background: 'linear-gradient(90deg,#005c53,#00a99d,#005c53)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                現場以外の仕事を、もっとラクに。
                            </span>
                        </h2>
                        <p style={{ color: '#6b7280', fontSize: '1rem' }}>機能名より、使うとどう楽になるかをお伝えします</p>
                    </FadeSection>

                    <div className="clp-features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
                        {features.map(({ Icon, effect, desc, iconBg }, i) => (
                            <FadeSection key={i} delay={i * 0.08}>
                                <div className="clp-feature-card">
                                    <div style={{ display: 'inline-flex', padding: 14, background: iconBg, borderRadius: 14, marginBottom: 18 }}>
                                        <Icon />
                                    </div>
                                    <h3 style={{ fontSize: '1.02rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 10, lineHeight: 1.5 }}>{effect}</h3>
                                    <p style={{ fontSize: '0.86rem', color: '#6b7280', lineHeight: 1.7, margin: 0 }}>{desc}</p>
                                </div>
                            </FadeSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── 仕事の流れ ─────────────────────────────────────────── */}
            <section id="flow" style={{ background: 'linear-gradient(180deg,#f5f6f8 0%,#fff 100%)', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
                    <FadeSection style={{ textAlign: 'center', marginBottom: 56 }}>
                        <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, marginBottom: 12 }}>
                            <span style={{ background: 'linear-gradient(90deg,#005c53,#00a99d)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                登録すると、仕事はこんなにシンプルになります。
                            </span>
                        </h2>
                        <p style={{ color: '#6b7280', fontSize: '1rem' }}>営業〜入金まで、バラバラだった作業がひとつの流れに</p>
                    </FadeSection>

                    <div className="clp-workflow-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 12 }}>
                        {workflowSteps.map((step, i) => (
                            <FadeSection key={i} delay={i * 0.08}>
                                <div className="clp-step-wrap" style={{ position: 'relative' }}>
                                    <div className="clp-step-circle" style={{ background: `linear-gradient(135deg,${step.color},${step.color}bb)`, boxShadow: `0 4px 16px ${step.color}44` }}>
                                        {step.n}
                                    </div>
                                    <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 4, whiteSpace: 'pre-line', lineHeight: 1.4 }}>{step.title}</h3>
                                    <p style={{ fontSize: '0.74rem', color: '#6b7280', margin: 0 }}>{step.desc}</p>
                                    {i < workflowSteps.length - 1 && (
                                        <div style={{ position: 'absolute', top: 24, right: -10, display: 'none' }} className="clp-step-arrow">
                                            <ArrowRightIcon color="rgba(107,114,128,0.35)" size={16} />
                                        </div>
                                    )}
                                </div>
                            </FadeSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Before/After ───────────────────────────────────────── */}
            <section style={{ background: '#fff', padding: '80px 0' }}>
                <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px' }}>
                    <FadeSection style={{ textAlign: 'center', marginBottom: 48 }}>
                        <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, color: '#1a1a2e' }}>
                            登録前と登録後で、これだけ変わります。
                        </h2>
                    </FadeSection>

                    <div className="clp-compare-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                        <FadeSection>
                            <div style={{ background: '#f5f6f8', border: '1px solid #e0e4ea', borderRadius: 12, padding: '28px 24px', height: '100%', boxSizing: 'border-box' }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#6b7280', marginBottom: 18 }}>今まで</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    {beforeItems.map((item, i) => (
                                        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                            <span style={{ marginTop: 2, flexShrink: 0 }}><CheckIcon /></span>
                                            <span style={{ fontSize: '0.88rem', color: '#6b7280', lineHeight: 1.6 }}>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </FadeSection>
                        <FadeSection delay={0.1}>
                            <div style={{ background: 'linear-gradient(135deg,#f0fffe,#e6f2f1)', border: '2px solid #005c53', borderRadius: 12, padding: '28px 24px', height: '100%', boxSizing: 'border-box', boxShadow: '0 8px 24px rgba(0,92,83,0.12)' }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#005c53', marginBottom: 18 }}>登録後</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    {afterItems.map((item, i) => (
                                        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                            <span style={{ marginTop: 2, flexShrink: 0 }}><CheckTealIcon /></span>
                                            <span style={{ fontSize: '0.88rem', color: '#1a1a2e', fontWeight: 500, lineHeight: 1.6 }}>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </FadeSection>
                    </div>
                </div>
            </section>

            {/* ── FAQ ──────────────────────────────────────────────── */}
            <section style={{ background: 'linear-gradient(180deg,#f5f6f8,#fff)', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle,rgba(0,169,157,0.08),transparent)', filter: 'blur(48px)' }} />
                <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
                    <FadeSection style={{ textAlign: 'center', marginBottom: 48 }}>
                        <div style={{ display: 'inline-block', padding: '6px 18px', background: 'linear-gradient(90deg,#005c53,#00a99d)', color: '#fff', borderRadius: 20, fontSize: '0.82rem', fontWeight: 700, marginBottom: 16 }}>
                            FAQ
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>よくある質問</h2>
                    </FadeSection>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {faqs.map((faq, i) => (
                            <FadeSection key={i} delay={i * 0.05}>
                                <div className="clp-faq-item">
                                    <button className="clp-faq-btn" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                                        <span style={{ fontWeight: 500, color: '#1a1a2e', fontSize: '0.95rem', paddingRight: 16 }}>
                                            {faq.q}
                                        </span>
                                        <span className="clp-faq-chevron">
                                            <ChevronDownIcon open={openFaq === i} />
                                        </span>
                                    </button>
                                    <div className="clp-faq-answer" style={{ maxHeight: openFaq === i ? 200 : 0, opacity: openFaq === i ? 1 : 0 }}>
                                        <div style={{ padding: '0 24px 20px' }}>
                                            <div style={{ background: 'linear-gradient(90deg,rgba(230,242,241,0.5),transparent)', borderLeft: '4px solid #005c53', borderRadius: '0 8px 8px 0', padding: '12px 16px', color: '#1a1a2e', fontSize: '0.9rem', lineHeight: 1.7 }}>
                                                {faq.a}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </FadeSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── 最終CTA ────────────────────────────────────────────── */}
            <section style={{ background: 'linear-gradient(135deg,#141c3d,#1a2847,#141c3d)', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, right: 0, width: 360, height: 360, borderRadius: '50%', background: '#fbbf24', filter: 'blur(80px)', animation: 'orbBreath1 8s ease-in-out infinite' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, width: 360, height: 360, borderRadius: '50%', background: '#00a99d', filter: 'blur(80px)', animation: 'orbBreath2 10s ease-in-out infinite' }} />

                <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', position: 'relative', textAlign: 'center' }}>
                    <FadeSection>
                        <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.1rem)', fontWeight: 700, color: '#fff', marginBottom: 12, lineHeight: 1.5 }}>
                            面倒な事務作業と営業を減らして、<br />もっと現場の仕事に集中しませんか？
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '1rem', marginBottom: 32 }}>
                            まずは施工業者登録から始めてください。
                        </p>
                        <Link to="/contractor/login?tab=register" className="clp-cta-final">
                            施工業者登録をはじめる <ArrowRightIcon color="#005c53" />
                        </Link>
                        <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', marginTop: 14 }}>登録は数分で完了します</p>
                    </FadeSection>
                </div>
            </section>

        </div>
    );
};

export default ContractorLanding;
